import {useDisclosure} from '@chakra-ui/react'
import isEmpty from 'lodash/isEmpty'
import dynamic from 'next/dynamic'
import {useEffect} from 'react'
import {SELECTED_GENERATION_OPTION} from '../../constants/playground'
import ModelExample from '../../dataModels/ModelExample'
import ModelExampleCategory from '../../dataModels/ModelExampleCategory'
import {Model} from '../../dataModels/Service'
import axios from '../../fetchers/axiosAPI'
import {useAuth} from '../../hooks/useAuth'
import {useIsMobile} from '../../hooks/useIsMobile'
import {useSignOut} from '../../hooks/useSignOut'
import modelStore from '../../stores/modelStore'

const LoginModal = dynamic(() => import('../Modals/LoginModal'))
const DesktopTextEditor = dynamic(() => import('./DesktopTextEditor'))
const MobileTextEditor = dynamic(() => import('./MobileTextEditor'))
const GenerationLimitReachedModal = dynamic(() => import('../Modals/GenerationLimitReachedModal'))

interface Props {
  model: Model
  modelExamples: ModelExample[]
  modelExamplesCategories: ModelExampleCategory[]
}

const ModelInput = ({model, modelExamples, modelExamplesCategories}: Props) => {
  const [, signOut] = useSignOut()

  const isMobile = useIsMobile()

  const input = modelStore(state => state.input)
  const output = modelStore(state => state.output)
  const autoReset = modelStore(state => state.autoReset)
  const setInput = modelStore(state => state.setInput)
  const setOutput = modelStore(state => state.setOutput)
  const setIsToxic = modelStore(state => state.setIsToxic)
  const setDefaults = modelStore(state => state.setDefaults)
  const setIsGenerating = modelStore(state => state.setIsGenerating)
  const setSelectedGenerationType = modelStore(state => state.setSelectedGenerationType)

  const {user, session} = useAuth().state
  const loginNeeded = useDisclosure()
  const limitReached = useDisclosure()

  useEffect(() => {
    if (isEmpty(input)) {
      onDelete()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input])

  const generateContent = async (promptOverride?: string) => {
    if (!user) return
    if (!session || isEmpty(session.access_token)) {
      await signOut()
      return
    }
    try {
      const realInput = promptOverride ?? input
      const response = await axios.post(model.endpoint, {
        input: realInput.substring(realInput.length - 500),
      })
      if (response && response.data.error) {
        const showToast = await import('../../utils/showToast').then(mod => mod.showToast)
        showToast(response.data.error, 'error')
      }

      if (response && response.data.output) {
        setIsToxic(response.data.isToxic)
        setOutput(response.data.output)
      }
      setIsGenerating(false)
    } catch (e: any) {
      if (e.response.status === 403) {
        limitReached.onOpen()
      } else {
        const showToast = await import('../../utils/showToast').then(mod => mod.showToast)
        showToast(e.response.data.error, 'error')
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const onDelete = () => {
    setOutput('')
    setInput('')
    setIsToxic(false)
    setSelectedGenerationType(SELECTED_GENERATION_OPTION.GENERATE_FIRST)
    if (autoReset) {
      setDefaults()
    }
  }

  const preGenerate = () => {
    setIsGenerating(true)
    setIsToxic(false)
    setOutput('')
  }

  const onGenerate = async () => {
    setInput(input)
    if (user) {
      preGenerate()
      await generateContent()
    } else {
      loginNeeded.onOpen()
    }
  }

  const onGenerateMore = async () => {
    preGenerate()
    const newInput = input.concat(`\n` + output)
    setInput(newInput)
    await generateContent(newInput)
  }

  const onRedo = async () => {
    preGenerate()
    await generateContent()
  }

  return (
    <>
      {isMobile && (
        <MobileTextEditor
          modelExamples={modelExamples}
          modelExamplesCategories={modelExamplesCategories}
          onDelete={onDelete}
          onGenerate={onGenerate}
          onGenerateMore={onGenerateMore}
          onRedo={onRedo}
        />
      )}
      {!isMobile && (
        <DesktopTextEditor
          modelExamples={modelExamples}
          modelExamplesCategories={modelExamplesCategories}
          onDelete={onDelete}
          onGenerate={onGenerate}
          onGenerateMore={onGenerateMore}
          onRedo={onRedo}
        />
      )}
      {loginNeeded.isOpen && (
        <LoginModal isOpen={loginNeeded.isOpen} onClose={loginNeeded.onClose} />
      )}
      {limitReached.isOpen && (
        <GenerationLimitReachedModal isOpen={limitReached.isOpen} onClose={limitReached.onClose} />
      )}
    </>
  )
}

export default ModelInput
